// Las métricas abajo de la lista de lanzamientos, en la misma página —
// ADR-2026-10-06-metricas-y-lanzamientos-en-una-pagina, ADR-2026-10-06-metricas-de-tienda-en-el-backend.
//
// Lee las tiendas REALES de la marca (`GET /v1/metricas/tienda`) y elige qué mostrar:
//  - ninguna conectada → la invitación a conectar (`MetricasSinTiendas`);
//  - una → esa tienda sola, sin bloque "Global": una suma de una sola cosa no dice nada
//    (ADR-2026-10-06-metricas-con-una-sola-tienda);
//  - las dos → una card con lo GLOBAL (la suma de lo que las dos informan igual) y, adentro,
//    POR TIENDA (lo de cada una y lo que sólo ella sabe).
//
// Nada inventado: lo que una tienda no deja leer (permiso que falta) o no contestó se dice con
// su motivo y la forma de arreglarlo, en vez de un cero que se leería como "no vendiste nada".
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Clock,
  Globe2,
  Lock,
  MessageCircleQuestion,
  RefreshCw,
  ShoppingCart,
  Star,
  Store,
  UserPlus,
} from 'lucide-react';
import IconoDeCanal from '../IconoDeCanal';
import { boton } from '../botones';
import { SelectorDePeriodo } from './comunes';
import { useMetricasDeTienda } from './metricasApi';
import { numero, pesosCortos, porcentaje } from './formato';
import { TarjetaKpi } from './graficos';
import {
  COLOR_DE_PLATAFORMA,
  NOMBRE_DE_PLATAFORMA,
  faltaDe,
  ingresoNeto,
  type EstadoDeTienda,
  type Faltante,
  type MetricasDePlataforma,
  type Periodo,
  type Plataforma,
  type RespuestaMetricas,
} from './tipos';

const PLATAFORMAS: Plataforma[] = ['tiendanube', 'mercadolibre'];

/** Conectada = tiene una conexión, aunque haya que reconectarla o no haya contestado. */
function estaConectada(e: EstadoDeTienda): boolean {
  return e.estado === 'ok' || e.estado === 'reconectar' || e.estado === 'error';
}

export function ResumenDeMetricas({ forzarSinTiendas = false }: { forzarSinTiendas?: boolean }) {
  const location = useLocation();
  // Volviendo del detalle de una tienda, el período sigue siendo el que se eligió.
  const [periodo, setPeriodo] = useState<Periodo>(
    ((location.state as { periodo?: Periodo } | null)?.periodo as Periodo) ?? 30,
  );
  const { datos, cargando, error, recargar } = useMetricasDeTienda(periodo, !forzarSinTiendas);

  // Ensayo de primera vez: como alguien sin tiendas, sin preguntarle nada al backend.
  if (forzarSinTiendas) return <MetricasSinTiendas plataformas={PLATAFORMAS} />;
  if (!datos) {
    return error ? <ErrorDeMetricas onReintentar={recargar} /> : <EsqueletoDeMetricas />;
  }

  const disponibles = PLATAFORMAS.filter((p) => datos[p].estado !== 'no_disponible');
  const conectadas = disponibles.filter((p) => estaConectada(datos[p]));
  const encabezado = (
    <Encabezado periodo={periodo} onPeriodo={setPeriodo} actualizando={cargando} onActualizar={recargar} datos={datos} />
  );

  if (conectadas.length === 0) return <MetricasSinTiendas plataformas={disponibles} />;
  if (conectadas.length === 1) {
    return (
      <ResumenDeUnaTienda
        plataforma={conectadas[0]!}
        estado={datos[conectadas[0]!]}
        otras={disponibles.filter((p) => p !== conectadas[0])}
        periodo={datos.dias}
        encabezado={encabezado}
      />
    );
  }
  // Los números se rotulan con el período del que SON (`datos.dias`), no con el que se acaba de
  // elegir y todavía está cargando.
  return <ResumenConDosTiendas datos={datos} periodo={datos.dias} encabezado={encabezado} />;
}

// ── Piezas comunes ─────────────────────────────────────────────────────────────────────

function Encabezado({
  periodo,
  onPeriodo,
  actualizando,
  onActualizar,
  datos,
  bajada,
}: {
  periodo: Periodo;
  onPeriodo: (p: Periodo) => void;
  actualizando: boolean;
  onActualizar: () => void;
  datos: RespuestaMetricas;
  bajada?: string;
}) {
  const hora = new Date(datos.generadoAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 id="titulo-metricas" className="text-lg font-semibold text-brand-black">
          Cómo vienen tus ventas
        </h2>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-neutral-500">
          {bajada ?? `Últimos ${datos.dias} días, comparados con los ${datos.dias} anteriores.`}
          {/* De cuándo son los números, y cómo traerlos de nuevo: se guardan unos minutos. */}
          <button
            type="button"
            onClick={onActualizar}
            disabled={actualizando}
            className="inline-flex items-center gap-1 text-xs text-neutral-400 transition-colors hover:text-[#1644EB] disabled:cursor-default"
          >
            <RefreshCw className={`h-3 w-3 ${actualizando ? 'animate-spin' : ''}`} />
            {actualizando ? 'Actualizando…' : `Actualizado ${hora}`}
          </button>
        </p>
      </div>
      <SelectorDePeriodo valor={periodo} onCambiar={onPeriodo} />
    </div>
  );
}

/** Lo que trae cada tienda, para invitar a conectarla. */
const LO_QUE_TRAE: Record<Plataforma, string> = {
  tiendanube: 'Ventas, carritos abandonados, clientes nuevos y stock',
  mercadolibre: 'Ventas, ingreso neto, visitas, conversión y reputación',
};

/** Una tienda sin conectar: qué trae y la puerta a Mi marca, donde se conectan siempre. */
function FilaParaConectar({ plataforma, detalle }: { plataforma: Plataforma; detalle?: string }) {
  const navigate = useNavigate();
  return (
    <div
      className="flex items-center gap-4 rounded-2xl border border-white bg-white/90 p-4 shadow-sm"
      style={{ borderLeft: `3px solid ${COLOR_DE_PLATAFORMA[plataforma]}` }}
    >
      <IconoDeCanal canal={plataforma} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-brand-black">{NOMBRE_DE_PLATAFORMA[plataforma]}</span>
        <span className="block text-xs text-neutral-500">{detalle ?? LO_QUE_TRAE[plataforma]}</span>
      </span>
      <button
        type="button"
        onClick={() => navigate('/mi-marca')}
        className={boton('secundario', { chico: true, extra: 'shrink-0 py-2' })}
      >
        Conectar
      </button>
    </div>
  );
}

/**
 * Lo que falta de una métrica, dicho como se arregla: sin permiso → volver a conectar (en Mi
 * marca); no disponible → todavía no se lee; error → reintentar.
 */
export function AvisoDeFalta({
  faltante,
  onReintentar,
  compacto = false,
}: {
  faltante: Pick<Faltante, 'motivo' | 'detalle'>;
  onReintentar?: () => void;
  compacto?: boolean;
}) {
  const navigate = useNavigate();
  const Icono = faltante.motivo === 'sin_permiso' ? Lock : faltante.motivo === 'no_disponible' ? Clock : AlertTriangle;
  return (
    <div
      className={`flex items-start gap-3 rounded-xl ${
        faltante.motivo === 'error' ? 'bg-amber-50 text-amber-900' : 'bg-neutral-50 text-neutral-700'
      } ${compacto ? 'p-3' : 'p-4'}`}
    >
      <Icono className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
      <div className="min-w-0 flex-1">
        <p className="text-xs leading-relaxed">{faltante.detalle}</p>
        {faltante.motivo === 'sin_permiso' && (
          <button
            type="button"
            onClick={() => navigate('/mi-marca')}
            className="mt-2 text-xs font-medium text-[#1644EB] hover:underline"
          >
            Volver a conectar en Mi marca
          </button>
        )}
        {faltante.motivo === 'error' && onReintentar && (
          <button type="button" onClick={onReintentar} className="mt-2 text-xs font-medium text-[#1644EB] hover:underline">
            Reintentar
          </button>
        )}
      </div>
    </div>
  );
}

function EsqueletoDeMetricas() {
  return (
    <section aria-busy="true" aria-label="Cargando tus ventas" className="mt-8">
      <div className="h-6 w-56 animate-pulse rounded-full bg-neutral-200" />
      <div className="mt-2 h-4 w-72 animate-pulse rounded-full bg-neutral-100" />
      <div className="mt-5 grid grid-cols-2 gap-3 rounded-3xl border border-[#1644EB]/10 bg-[#F3F6FF] p-4 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[124px] animate-pulse rounded-2xl bg-white" />
        ))}
      </div>
    </section>
  );
}

function ErrorDeMetricas({ onReintentar }: { onReintentar: () => void }) {
  return (
    <section className="mt-8">
      <h2 id="titulo-metricas" className="text-lg font-semibold text-brand-black">
        Cómo vienen tus ventas
      </h2>
      <div className="mt-4">
        <AvisoDeFalta
          faltante={{ motivo: 'error', detalle: 'No pudimos traer las métricas de tus tiendas.' }}
          onReintentar={onReintentar}
        />
      </div>
    </section>
  );
}

/**
 * Sin ninguna tienda conectada: no hay nada que medir, y un tablero en cero se lee como
 * "no vendiste nada". Se muestra cómo va a quedar —los mismos cuatro números, vacíos— y
 * la puerta para conectar cada tienda (ADR-2026-10-06-ensayo-de-primera-vez).
 */
function MetricasSinTiendas({ plataformas }: { plataformas: Plataforma[] }) {
  return (
    <section aria-labelledby="titulo-metricas" className="mt-8">
      <h2 id="titulo-metricas" className="text-lg font-semibold text-brand-black">
        Cómo vienen tus ventas
      </h2>
      <p className="mt-0.5 text-sm text-neutral-500">Todavía no hay ninguna tienda conectada.</p>

      <div
        data-tour="tienda-conectar"
        className="mt-5 overflow-hidden rounded-3xl border border-[#1644EB]/10 bg-gradient-to-br from-[#F3F6FF] via-white to-[#EFFCFD] p-5 md:p-8"
      >
        <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <div>
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#EEF2FF] text-[#1644EB]">
              <BarChart3 className="h-5 w-5" />
            </span>
            <p className="mt-4 text-lg font-semibold text-brand-black md:text-xl">
              {plataformas.length > 1 ? 'Conectá tus tiendas y mirá cómo vendés' : 'Conectá tu tienda y mirá cómo vendés'}
            </p>
            <p className="mt-1.5 max-w-md text-sm leading-relaxed text-neutral-500">
              Vas a ver tus ventas, órdenes y ticket promedio
              {plataformas.length > 1 ? ' de las dos tiendas sumadas, y lo de cada una por separado' : ''}. Los números se
              completan solos.
            </p>
            {/* Cómo va a quedar: los mismos cuatro números, sin inventar ninguno. */}
            <div className="mt-5 grid max-w-md grid-cols-2 gap-2" aria-hidden="true">
              {['Ventas', 'Órdenes', 'Ticket promedio', 'Unidades vendidas'].map((e) => (
                <div key={e} className="rounded-xl border border-dashed border-neutral-300 bg-white/70 py-2.5 px-3">
                  <p className="text-[11px] text-neutral-400">{e}</p>
                  <p className="text-lg font-semibold text-neutral-300">—</p>
                </div>
              ))}
            </div>
          </div>

          <ul className="space-y-3">
            {plataformas.map((p) => (
              <li key={p}>
                <FilaParaConectar plataforma={p} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/** Una tienda conectada que no se pudo leer entera: hay que reconectarla o no contestó. */
function EstadoNoLeido({ estado, onReintentar }: { estado: EstadoDeTienda; onReintentar?: () => void }) {
  if (estado.estado === 'reconectar') return <AvisoDeFalta faltante={{ motivo: 'sin_permiso', detalle: estado.motivo }} />;
  if (estado.estado === 'error') return <AvisoDeFalta faltante={{ motivo: 'error', detalle: estado.detalle }} onReintentar={onReintentar} />;
  return null;
}

// ── Una sola tienda ────────────────────────────────────────────────────────────────────

/** Los cuatro números grandes de una tienda, con variación. Sin ventas leídas, el aviso de por qué. */
function KpisDeLaTienda({ d, periodo }: { d: MetricasDePlataforma; periodo: Periodo }) {
  const contra = `vs. ${periodo} días anteriores`;
  const color = COLOR_DE_PLATAFORMA[d.plataforma];
  const falta = faltaDe(d, 'ventas');
  if (!d.actual || !d.anterior) {
    // Mercado Libre sin ventas igual puede tener visitas: se muestran solas, con el aviso de ventas al lado.
    return (
      <div className={`grid gap-3 ${d.visitas ? 'lg:grid-cols-[1fr_2fr]' : ''}`}>
        {d.visitas ? (
          <TarjetaKpi
            etiqueta="Visitas"
            valor={numero(d.visitas.actual)}
            actual={d.visitas.actual}
            anterior={d.visitas.anterior}
            contra={contra}
            serie={d.visitas.serie}
            colorSerie={color}
          />
        ) : null}
        {falta && <AvisoDeFalta faltante={falta} />}
      </div>
    );
  }
  const a = d.actual;
  const b = d.anterior;
  const ticket = a.ordenes ? a.ventas / a.ordenes : 0;
  const ticketAntes = b.ordenes ? b.ventas / b.ordenes : 0;
  if (d.plataforma === 'tiendanube') {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <TarjetaKpi etiqueta="Ventas" valor={pesosCortos(a.ventas)} actual={a.ventas} anterior={b.ventas} contra={contra} serie={d.serie?.map((p) => p.ventas)} colorSerie={color} ayuda="Órdenes pagas, sin canceladas" />
        <TarjetaKpi etiqueta="Órdenes" valor={numero(a.ordenes)} actual={a.ordenes} anterior={b.ordenes} contra={contra} />
        <TarjetaKpi etiqueta="Ticket promedio" valor={pesosCortos(ticket)} actual={ticket} anterior={ticketAntes} contra={contra} />
        <TarjetaKpi etiqueta="Unidades vendidas" valor={numero(a.unidades)} actual={a.unidades} anterior={b.unidades} contra={contra} />
      </div>
    );
  }
  const neto = ingresoNeto(d, 'actual') ?? 0;
  const conv = d.visitas ? a.ordenes / Math.max(1, d.visitas.actual) : null;
  const convAntes = d.visitas ? b.ordenes / Math.max(1, d.visitas.anterior) : 0;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <TarjetaKpi etiqueta="Ventas" valor={pesosCortos(a.ventas)} actual={a.ventas} anterior={b.ventas} contra={contra} serie={d.serie?.map((p) => p.ventas)} colorSerie={color} ayuda="Órdenes pagas, sin canceladas" />
      <TarjetaKpi etiqueta="Ingreso neto" valor={pesosCortos(neto)} actual={neto} anterior={ingresoNeto(d, 'anterior') ?? 0} contra={contra} ayuda="Ventas menos la comisión de Mercado Libre. El envío que pagás todavía no se descuenta." />
      {d.visitas ? (
        <TarjetaKpi etiqueta="Visitas" valor={numero(d.visitas.actual)} actual={d.visitas.actual} anterior={d.visitas.anterior} contra={contra} />
      ) : (
        <TarjetaKpi etiqueta="Órdenes" valor={numero(a.ordenes)} actual={a.ordenes} anterior={b.ordenes} contra={contra} />
      )}
      {conv !== null ? (
        <TarjetaKpi etiqueta="Conversión" valor={porcentaje(conv)} actual={conv} anterior={convAntes} contra={contra} ayuda="Órdenes cada 100 visitas" />
      ) : (
        <TarjetaKpi etiqueta="Ticket promedio" valor={pesosCortos(ticket)} actual={ticket} anterior={ticketAntes} contra={contra} />
      )}
    </div>
  );
}

/**
 * Una sola tienda conectada. Sin "Global" ni "Por tienda": la tienda ES el total. Sus
 * números van grandes y con su variación (como en el detalle), lo que sólo ella informa
 * abajo, y al pie las otras para conectar, diciendo qué se suma al hacerlo.
 */
function ResumenDeUnaTienda({
  plataforma,
  estado,
  otras,
  periodo,
  encabezado,
}: {
  plataforma: Plataforma;
  estado: EstadoDeTienda;
  otras: Plataforma[];
  periodo: Periodo;
  encabezado: React.ReactNode;
}) {
  const d = estado.estado === 'ok' ? estado.metricas : null;
  const color = COLOR_DE_PLATAFORMA[plataforma];
  return (
    <section aria-labelledby="titulo-metricas" className="mt-8">
      {encabezado}

      <div
        className="mt-5 overflow-hidden rounded-3xl border border-neutral-200 bg-gradient-to-br from-[#F3F6FF] to-white"
        style={{ borderTop: `3px solid ${color}` }}
      >
        <div className="flex flex-wrap items-center gap-3 border-b border-neutral-100 p-4 sm:px-5">
          <IconoDeCanal canal={plataforma} />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-brand-black">{NOMBRE_DE_PLATAFORMA[plataforma]}</h3>
            {d && <p className="truncate text-xs text-neutral-500">{d.cuenta}</p>}
          </div>
          {d && (
            <Link
              to={`/lanzamientos/metricas/${plataforma}`}
              state={{ periodo }}
              className="inline-flex items-center gap-1.5 rounded-xl py-2 px-3 text-sm font-medium text-[#1644EB] transition-colors hover:bg-[#EEF2FF]"
            >
              Ver todo <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        <div className="p-3 sm:p-4">
          {d ? (
            <>
              <KpisDeLaTienda d={d} periodo={periodo} />
              <ListaSoloDeEsta d={d} />
            </>
          ) : (
            <EstadoNoLeido estado={estado} />
          )}
        </div>
      </div>

      {/* Las otras tiendas: qué se suma si las conecta. Discreto, abajo: no compite con los números. */}
      {otras.length > 0 && (
        <div className="mt-3 rounded-3xl border border-dashed border-neutral-300 p-3 sm:p-4">
          <p className="mb-3 px-1 text-xs text-neutral-500">
            ¿Vendés también en {otras.map((o) => NOMBRE_DE_PLATAFORMA[o]).join(' y ')}? Conectala y vas a ver las ventas de
            las tiendas sumadas, además de lo de cada una.
          </p>
          <div className="space-y-2">
            {otras.map((o) => (
              <FilaParaConectar key={o} plataforma={o} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// ── Las dos tiendas ────────────────────────────────────────────────────────────────────

function ResumenConDosTiendas({
  datos,
  periodo,
  encabezado,
}: {
  datos: RespuestaMetricas;
  periodo: Periodo;
  encabezado: React.ReactNode;
}) {
  const contra = `vs. ${periodo} días anteriores`;
  // Lo global suma SÓLO las tiendas de las que se pudieron leer ventas, y dice cuáles quedaron afuera.
  const conVentas = PLATAFORMAS.map((p) => datos[p])
    .filter((e): e is Extract<EstadoDeTienda, { estado: 'ok' }> => e.estado === 'ok')
    .map((e) => e.metricas)
    .filter((m) => m.actual && m.anterior);
  const sinVentas = PLATAFORMAS.filter((p) => !conVentas.some((m) => m.plataforma === p));
  const total = (k: 'ventas' | 'ordenes' | 'unidades', cual: 'actual' | 'anterior') =>
    conVentas.reduce((t, d) => t + d[cual]![k], 0);
  const ventas = total('ventas', 'actual');
  const ordenes = total('ordenes', 'actual');
  const ticket = ordenes ? ventas / ordenes : 0;
  const ticketAntes = total('ordenes', 'anterior') ? total('ventas', 'anterior') / total('ordenes', 'anterior') : 0;
  const largo = conVentas[0]?.serie?.length ?? 0;
  const ventasPorDia = Array.from({ length: largo }, (_, i) => conVentas.reduce((t, d) => t + (d.serie?.[i]?.ventas ?? 0), 0));

  return (
    <section aria-labelledby="titulo-metricas" className="mt-8">
      {encabezado}

      <div className="mt-5 rounded-3xl border border-[#1644EB]/10 bg-gradient-to-br from-[#F3F6FF] to-white p-3 sm:p-4">
        {/* ── Global ── */}
        <EncabezadoDeBloque
          icono={<Globe2 className="h-4 w-4" />}
          titulo="Global"
          detalle={
            conVentas.length === 2 ? (
              <span className="flex items-center gap-1.5">
                Suma de
                {PLATAFORMAS.map((p, i) => (
                  <span key={p} className="inline-flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full" style={{ background: COLOR_DE_PLATAFORMA[p] }} aria-hidden="true" />
                    {NOMBRE_DE_PLATAFORMA[p]}
                    {i === 0 && <span className="ml-0.5">+</span>}
                  </span>
                ))}
              </span>
            ) : conVentas.length === 1 ? (
              `Sólo ${NOMBRE_DE_PLATAFORMA[conVentas[0]!.plataforma]}: de ${sinVentas.map((p) => NOMBRE_DE_PLATAFORMA[p]).join(' y ')} todavía no podemos leer las ventas`
            ) : (
              'Todavía no podemos leer las ventas de ninguna de las dos tiendas'
            )
          }
        />
        {conVentas.length > 0 ? (
          <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <TarjetaKpi etiqueta="Ventas" valor={pesosCortos(ventas)} actual={ventas} anterior={total('ventas', 'anterior')} contra={contra} serie={ventasPorDia} ayuda="Órdenes pagas, sin canceladas" />
            <TarjetaKpi etiqueta="Órdenes" valor={numero(ordenes)} actual={ordenes} anterior={total('ordenes', 'anterior')} contra={contra} />
            <TarjetaKpi etiqueta="Ticket promedio" valor={pesosCortos(ticket)} actual={ticket} anterior={ticketAntes} contra={contra} />
            <TarjetaKpi etiqueta="Unidades vendidas" valor={numero(total('unidades', 'actual'))} actual={total('unidades', 'actual')} anterior={total('unidades', 'anterior')} contra={contra} />
          </div>
        ) : (
          <div className="mt-3">
            <AvisoDeFalta
              faltante={{
                motivo: 'sin_permiso',
                detalle:
                  'Las tiendas están conectadas, pero todavía no nos dieron permiso para leer las ventas. Cada tarjeta de abajo dice qué falta.',
              }}
            />
          </div>
        )}

        {/* ── Por tienda, adentro de la misma card ── */}
        <div className="mt-5 border-t border-[#1644EB]/10 pt-4">
          <EncabezadoDeBloque
            icono={<Store className="h-4 w-4" />}
            titulo="Por tienda"
            detalle="Lo de cada una por separado, incluido lo que sólo esa plataforma informa"
          />
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {PLATAFORMAS.map((p) => (
              <TarjetaDeTienda key={p} plataforma={p} estado={datos[p]} periodo={periodo} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function EncabezadoDeBloque({ icono, titulo, detalle }: { icono: React.ReactNode; titulo: string; detalle: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-white py-1 px-2.5 text-xs font-semibold uppercase tracking-wide text-brand-black shadow-sm ring-1 ring-neutral-200">
        <span className="text-[#1644EB]">{icono}</span>
        {titulo}
      </span>
      <span className="text-xs text-neutral-500">{detalle}</span>
    </div>
  );
}

const NIVEL = ['Roja', 'Naranja', 'Amarilla', 'Verde claro', 'Verde'];

/**
 * Lo que sólo informa esa plataforma, en dos renglones. Lo que no vino no se lista: el aviso de
 * permiso de arriba ya dice por qué, y una fila con "—" debajo sólo suma ruido.
 */
function soloDeEsta(d: MetricasDePlataforma): Array<{ icono: React.ReactNode; texto: string; valor: string }> {
  return filasSoloDeEsta(d).filter((f) => f.valor !== '—');
}

function filasSoloDeEsta(d: MetricasDePlataforma): Array<{ icono: React.ReactNode; texto: string; valor: string }> {
  if (d.plataforma === 'tiendanube') {
    return [
      {
        icono: <ShoppingCart className="h-4 w-4" />,
        texto: 'Carritos abandonados (últimos 30 días)',
        valor: d.carritosAbandonados
          ? `${numero(d.carritosAbandonados.cantidad)} · ${pesosCortos(d.carritosAbandonados.monto)}`
          : '—',
      },
      { icono: <UserPlus className="h-4 w-4" />, texto: 'Clientes nuevos', valor: d.clientes ? numero(d.clientes.nuevos) : '—' },
    ];
  }
  const r = d.reputacion;
  return [
    {
      icono: <Star className="h-4 w-4" />,
      texto: 'Reputación',
      valor: r ? `${r.nivel ? NIVEL[r.nivel - 1] : 'Sin reputación todavía'}${r.medalla ? ' · MercadoLíder' : ''}` : '—',
    },
    {
      icono: <MessageCircleQuestion className="h-4 w-4" />,
      texto: 'Preguntas sin responder',
      valor: d.preguntasSinResponder !== undefined ? numero(d.preguntasSinResponder) : '—',
    },
  ];
}

function ListaSoloDeEsta({ d }: { d: MetricasDePlataforma }) {
  const filas = soloDeEsta(d);
  if (filas.length === 0) return null;
  return (
    <ul className="mt-3 grid gap-2 sm:grid-cols-2">
      {filas.map((e) => (
        <li key={e.texto} className="flex items-center justify-between gap-3 rounded-xl bg-white py-2.5 px-3 text-xs shadow-sm ring-1 ring-neutral-200">
          <span className="flex min-w-0 items-center gap-2 text-neutral-500">
            <span className="text-neutral-400">{e.icono}</span>
            <span className="truncate">{e.texto}</span>
          </span>
          <span className="shrink-0 font-medium text-brand-black">{e.valor}</span>
        </li>
      ))}
    </ul>
  );
}

/** Una tienda dentro de "Por tienda": su color arriba, sus números, lo que sólo ella informa y su detalle. */
function TarjetaDeTienda({ plataforma, estado, periodo }: { plataforma: Plataforma; estado: EstadoDeTienda; periodo: Periodo }) {
  const d = estado.estado === 'ok' ? estado.metricas : null;
  return (
    <section
      className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card shadow-sm"
      style={{ borderTop: `3px solid ${COLOR_DE_PLATAFORMA[plataforma]}` }}
    >
      <div className="flex items-center gap-3 border-b border-neutral-100 p-4 sm:px-5">
        <IconoDeCanal canal={plataforma} />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-brand-black">{NOMBRE_DE_PLATAFORMA[plataforma]}</h3>
          {d && <p className="truncate text-xs text-neutral-500">{d.cuenta}</p>}
        </div>
      </div>
      <div className="flex-1 p-4 sm:px-5">
        {!d ? (
          <EstadoNoLeido estado={estado} />
        ) : (
          <>
            <NumerosDeLaTarjeta d={d} />
            {soloDeEsta(d).length > 0 && (
            <div className="mt-4 border-t border-neutral-100 pt-3">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-400">
                Sólo de {NOMBRE_DE_PLATAFORMA[plataforma]}
              </p>
              <ul className="space-y-2">
                {soloDeEsta(d).map((e) => (
                  <li key={e.texto} className="flex items-center justify-between gap-3 text-xs">
                    <span className="flex min-w-0 items-center gap-2 text-neutral-500">
                      <span className="text-neutral-400">{e.icono}</span>
                      <span className="truncate">{e.texto}</span>
                    </span>
                    <span className="shrink-0 font-medium text-brand-black">{e.valor}</span>
                  </li>
                ))}
              </ul>
            </div>
            )}
          </>
        )}
      </div>
      {d && (
        <Link
          to={`/lanzamientos/metricas/${plataforma}`}
          state={{ periodo }}
          className="flex items-center justify-between gap-2 border-t border-neutral-100 py-3 px-4 text-sm font-medium text-[#1644EB] transition-colors hover:bg-[#EEF2FF] sm:px-5"
        >
          Ver todo de {NOMBRE_DE_PLATAFORMA[plataforma]}
          <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </section>
  );
}

/** Los cuatro números de la tarjeta. Sin ventas leídas, el aviso de por qué (y lo que sí haya). */
function NumerosDeLaTarjeta({ d }: { d: MetricasDePlataforma }) {
  const a = d.actual;
  const conversion = a && d.visitas ? a.ordenes / Math.max(1, d.visitas.actual) : null;
  const neto = ingresoNeto(d, 'actual');
  const datos: Array<{ etiqueta: string; valor: string }> =
    d.plataforma === 'tiendanube'
      ? a
        ? [
            { etiqueta: 'Ventas', valor: pesosCortos(a.ventas) },
            { etiqueta: 'Órdenes', valor: numero(a.ordenes) },
            { etiqueta: 'Ticket promedio', valor: pesosCortos(a.ordenes ? a.ventas / a.ordenes : 0) },
            { etiqueta: 'Unidades', valor: numero(a.unidades) },
          ]
        : []
      : [
          ...(a ? [{ etiqueta: 'Ventas', valor: pesosCortos(a.ventas) }] : []),
          ...(neto !== null ? [{ etiqueta: 'Ingreso neto', valor: pesosCortos(neto) }] : []),
          ...(d.visitas ? [{ etiqueta: 'Visitas', valor: numero(d.visitas.actual) }] : []),
          ...(conversion !== null ? [{ etiqueta: 'Conversión', valor: porcentaje(conversion) }] : []),
        ];
  const falta = !a ? faltaDe(d, 'ventas') : undefined;
  return (
    <>
      {datos.length > 0 && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4 md:grid-cols-2 xl:grid-cols-4">
          {datos.map((x) => (
            <div key={x.etiqueta} className="min-w-0">
              <p className="text-xs text-neutral-500">{x.etiqueta}</p>
              <p className="truncate text-lg font-semibold text-brand-black">{x.valor}</p>
            </div>
          ))}
        </div>
      )}
      {falta && (
        <div className={datos.length > 0 ? 'mt-3' : ''}>
          <AvisoDeFalta faltante={falta} compacto />
        </div>
      )}
    </>
  );
}
